import { downloadInspectionBudgetPdf } from '_/modules/inspectionBudget/actions'
import { EXCLUDED_TYPES_EXECUTION_TIME, LABOR_TYPE_SLUGS } from './constants'

export const normalizeText = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()

const normalizeCityValue = value =>
  normalizeText(
    String(value || '')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
  ).replace(/\s+/g, ' ')

export const convertInspectionCityToSlug = city =>
  normalizeText(city).replace(/\s+/g, '_').replace(/_+/g, '_')

export const PDF_EXPORT_MODES = {
  IDEAL: 'ideal',
  MAXIMUM: 'maximum',
}

const normalizeRegionValue = value => normalizeText(value).replace(/\s+/g, ' ')

const normalizeCitySlugValue = value => normalizeCityValue(value).replace(/\s+/g, '_')

const getCityExceptionRegion = cityException => {
  if (!cityException) return { city: '', state: '' }

  if (typeof cityException === 'string') {
    return {
      city: cityException,
      state: '',
    }
  }

  return {
    city:
      cityException?.city || cityException?.citySlug || cityException?.slug || cityException?.value,
    state: cityException?.state || cityException?.uf || '',
  }
}

const isMatchingCityException = ({ city, state, cityException }) => {
  const normalizedCity = normalizeCityValue(city)
  const normalizedCitySlug = normalizeCitySlugValue(city)
  const normalizedState = normalizeRegionValue(state)
  const { city: exceptionCityValue, state: exceptionStateValue } =
    getCityExceptionRegion(cityException)
  const exceptionCity = normalizeCityValue(exceptionCityValue)
  const exceptionCitySlug = normalizeCitySlugValue(exceptionCityValue)
  const exceptionState = normalizeRegionValue(exceptionStateValue)

  if (!normalizedCity || !exceptionCity) return false
  if (normalizedCity !== exceptionCity && normalizedCitySlug !== exceptionCitySlug) return false

  if (!normalizedState) return true
  if (!exceptionState) return true

  return normalizedState === exceptionState
}

export const getInspectionBudgetPdfExportMode = ({ city, state, cityExceptionList = [] }) => {
  const hasException = (cityExceptionList || []).some(cityException =>
    isMatchingCityException({ city, state, cityException })
  )

  return hasException ? PDF_EXPORT_MODES.MAXIMUM : PDF_EXPORT_MODES.IDEAL
}

export const getInspectionBudgetCitySlug = ({ city, state, citiesOptions = [] }) => {
  if (!city) return ''

  const normalizedCity = normalizeText(city)
  const normalizedState = normalizeText(state)

  const matchedCity = citiesOptions.find(option => {
    const isSameCity = normalizeText(option?.label) === normalizedCity

    if (!isSameCity) return false
    if (!normalizedState) return true

    return [option?.state?.uf, option?.state?.name].some(
      optionState => normalizeText(optionState) === normalizedState
    )
  })

  return matchedCity?.value || convertInspectionCityToSlug(city)
}

export const filterInspectionTradesmenByCity = ({
  tradesmen = [],
  city,
  state,
  citiesOptions = [],
}) => {
  const citySlug = getInspectionBudgetCitySlug({ city, state, citiesOptions })

  if (!citySlug) return []

  const possibleMatches = [citySlug, city, convertInspectionCityToSlug(city)]
    .filter(Boolean)
    .map(normalizeText)

  return tradesmen.filter(tradesman =>
    (Array.isArray(tradesman?.cities) ? tradesman.cities : []).some(providerCity =>
      possibleMatches.includes(normalizeText(providerCity))
    )
  )
}

export function getBudgetTotals(compartments = []) {
  return compartments.reduce(
    (totals, compartment) => {
      const resolutions = compartment?.inspectionBudgetResolutions || []

      const resolutionTotals = resolutions.reduce(
        (acc, resolution) => {
          const labor = Number(resolution?.laborValue) || 0
          const laborMax = Number(resolution?.laborValueMax) || 0

          const agencyValue = Number(resolution?.agencyValue) || 0

          const slug = resolution?.service?.laborType?.slug

          const laborTime = EXCLUDED_TYPES_EXECUTION_TIME.has(slug)
            ? 0
            : Number(resolution?.laborTime) || 0

          const materials = (resolution?.budgetResolutionMaterials || []).reduce(
            (sum, material) => {
              const quantity = Number(material?.quantity) || 0
              const unitPrice = Number(material?.unitPrice) || 0
              const total = Number((quantity * unitPrice).toFixed(2))
              return sum + total
            },
            0
          )

          return {
            laborTotal: acc.laborTotal + labor,
            laborMaxTotal: acc.laborMaxTotal + laborMax,
            laborTimeTotal: acc.laborTimeTotal + laborTime,
            materialsTotal: acc.materialsTotal + materials,
            agencyValuesTotal: acc.agencyValuesTotal + agencyValue,
          }
        },
        {
          laborTotal: 0,
          laborMaxTotal: 0,
          laborTimeTotal: 0,
          materialsTotal: 0,
          agencyValuesTotal: 0,
        }
      )

      return {
        laborTotal: totals.laborTotal + resolutionTotals.laborTotal,
        laborMaxTotal: totals.laborMaxTotal + resolutionTotals.laborMaxTotal,
        laborTimeTotal: totals.laborTimeTotal + resolutionTotals.laborTimeTotal,
        materialsTotal: totals.materialsTotal + resolutionTotals.materialsTotal,
        agencyValuesTotal: totals.agencyValuesTotal + resolutionTotals.agencyValuesTotal,
      }
    },
    { laborTotal: 0, laborMaxTotal: 0, laborTimeTotal: 0, materialsTotal: 0, agencyValuesTotal: 0 }
  )
}

export function getResolutionTotal(resolution) {
  const materialsTotal = (resolution?.budgetResolutionMaterials || []).reduce((sum, material) => {
    const unitPrice = Number(material?.unitPrice) || 0
    const quantity = Number(material?.quantity) || 0
    return sum + unitPrice * quantity
  }, 0)

  const laborValue = Number(resolution?.laborValue) || 0

  return materialsTotal + laborValue
}

const getInspectionBudgetPdfFileName = ({ id, pdfFileSuffix, pdfReferenceId }) => {
  const normalizedSuffix = Number(pdfFileSuffix)
  if (Number.isInteger(normalizedSuffix) && normalizedSuffix >= 0) {
    return `orcamento_vistoria_${id}_${normalizedSuffix}.pdf`
  }

  const normalizedReferenceId = Number(pdfReferenceId)
  const suffix = normalizedReferenceId >= 1 ? normalizedReferenceId - 1 : 0

  return `orcamento_vistoria_${id}_${suffix}.pdf`
}

export const handleDownloadPdf = async ({
  id,
  dispatch,
  showToast,
  setLoading,
  pdfFileSuffix,
  pdfReferenceId,
  exportMode,
}) => {
  setLoading(true)
  try {
    const blob = await dispatch(downloadInspectionBudgetPdf(id, { exportMode }))
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute(
      'download',
      getInspectionBudgetPdfFileName({
        id,
        pdfFileSuffix,
        pdfReferenceId,
      })
    )
    document.body.appendChild(link)
    link.click()
    link.parentNode.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    showToast({ type: 'error', message: 'Erro ao baixar o PDF' })
  } finally {
    setLoading(false)
  }
}

export const percentageToValue = (value, base) => (value / 100) * base

const getDailyAndPeriod = (dailyBase, rest) => {
  let periods = 0
  let extraDailys = 0

  if (rest > 0 && rest <= 4) {
    periods = 1
  }
  if (rest > 4) {
    extraDailys = 1
  }
  return {
    totalDailys: dailyBase + extraDailys,
    totalPeriods: periods,
  }
}

const groupedByLaborType = resolutions =>
  resolutions.reduce((acc, resolution) => {
    const laborType = resolution?.service?.laborType

    if (!laborType?.id) return acc

    const key = laborType?.id

    if (!acc[key]) {
      acc[key] = {
        laborType,
        totalMO: 0,
        dailyBase: 0,
        rest: 0,
        dailyValue: 0,
        periodValue: 0,
        totalMoValue: 0,
        totalMoValueMax: 0,
      }
    }

    acc[key].totalMO += Number(resolution?.laborTime || 0)
    acc[key].dailyBase = Math.floor(acc[key].totalMO / 8)
    acc[key].rest = acc[key].totalMO % 8
    const { totalDailys, totalPeriods } = getDailyAndPeriod(acc[key].dailyBase, acc[key].rest)
    acc[key].dailyValue = totalDailys
    acc[key].periodValue = totalPeriods
    acc[key].totalMoValue =
      totalDailys * laborType.dailyIdeal + totalPeriods * laborType.periodIdeal
    acc[key].totalMoValueMax = totalDailys * laborType.dailyMax + totalPeriods * laborType.periodMax

    return acc
  }, {})

export const getLaborValues = resolutions => {
  const laborGroups = groupedByLaborType(resolutions)
  const hasValidPainting = Object.values(laborGroups).some(
    group => group.laborType?.slug === LABOR_TYPE_SLUGS.PAINTING && group?.totalMO > 8
  )

  return { laborGroups, hasValidPainting }
}

export const getExecutionDue = laborTimeTotal => {
  const hours = (Number(laborTimeTotal) || 0) / 8
  return Math.ceil(hours + 1)
}

export const validateSqmOnRange = (squareMeter, services) => {
  const sqm = Number(squareMeter)

  if (!Number.isFinite(sqm) || sqm <= 0) return null

  for (const service of services || []) {
    const ranges = service?.laborType?.laborTimeBySquareMeterRanges

    if (ranges && ranges.length > 0) {
      const isInRange = ranges.some(r => {
        const start = Number(r.startSquareMeter)
        const end = Number(r.endSquareMeter)

        return sqm >= start && sqm <= end
      })

      if (!isInRange) {
        return service?.laborType?.name ?? '-'
      }
    }
  }

  return null
}

export const getLaborTime = (squareMeter, ranges) => {
  if (!ranges || ranges.length === 0) return 0
  const sqm = Number(squareMeter)
  if (!Number.isFinite(sqm) || sqm <= 0) return 0

  const range = ranges.find(r => {
    const start = Number(r.startSquareMeter)
    const end = Number(r.endSquareMeter)
    return sqm >= start && sqm <= end
  })

  if (range) return Number(range.laborTimeHours)

  return 0
}

export const getPaintingMaterial = squareMeter => {
  const sqm = Number(squareMeter)

  if (!Number.isFinite(sqm) || sqm < 0) return null

  const blockSize = 12.5
  const blockQty = 0.25

  const blocks = Math.max(1, Math.ceil(sqm / blockSize))

  return {
    quantity: Number((blocks * blockQty).toFixed(2)),
  }
}

export const toNumeric = value => {
  if (!value) return 0

  const num = Number(
    String(value)
      .replace(/[^\d,.-]/g, '')
      .replace(/\.(?=\d{3})/g, '')
      .replace(',', '.')
  )

  return Number.isFinite(num) ? Math.abs(num) : 0
}

export const roundTo2 = value => {
  const n = Number(value)
  if (!Number.isFinite(n)) return 0
  return Math.round((n + Number.EPSILON) * 100) / 100
}
